import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Paper,
  TableContainer,
  Grid
} from "@mui/material";
import { Edit, Delete, Visibility, Search, FilterList } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { blogService, categoryService } from "../api/services";
import { safeArray, safeMap } from "../utils/validation";
import { useAuth } from "../hooks/useAuth";

const PostManagementTable = ({ 
  defaultStatus = "", 
  defaultIsAutoGenerated = null,
  title = "Gestión de Posts"
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canEdit } = useAuth();
  const navigate = useNavigate();

  // Estados de datos
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Estados de filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [categoryFilter, setCategoryFilter] = useState("");

  // Estados de edición
  const [editOpen, setEditOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [postsRes, catsRes] = await Promise.all([
        blogService.getAllPosts(),
        categoryService.getCategories()
      ]);
      
      setPosts(safeArray(postsRes.data?.results || postsRes.data));
      setCategories(safeArray(catsRes.data));
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await blogService.updatePost(editPost.id, editPost);
      setEditOpen(false);
      fetchData(); // Recargar datos
    } catch (err) {
      console.error("Error updating post:", err);
      setError("Error al guardar cambios");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Seguro de eliminar este post?")) return;
    try {
      await blogService.deletePost(id);
      fetchData();
    } catch (err) {
      console.error("Error deleting post:", err);
      setError("Error al eliminar");
    }
  };

  // Filtrado
  const filteredPosts = posts.filter(post => {
    // Filtro por Status (si se especifica)
    if (statusFilter && post.status !== statusFilter) return false;
    
    // Filtro por Auto-Generado (si se especifica)
    if (defaultIsAutoGenerated !== null && post.is_auto_generated !== defaultIsAutoGenerated) return false;

    // Filtro por Categoría
    if (categoryFilter) {
      const postCats = safeArray(post.categories).map(c => c.id);
      if (!postCats.includes(categoryFilter)) return false;
    }

    // Búsqueda por texto
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        post.title?.toLowerCase().includes(term) ||
        post.excerpt?.toLowerCase().includes(term)
      );
    }

    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          {title}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/admin/ia")}>
          ➕ Crear Nuevo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Barra de Herramientas de Filtrado */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por título..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="published">Publicados</MenuItem>
              <MenuItem value="draft">Borradores</MenuItem>
              <MenuItem value="scheduled">Programados</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Categoría"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">Todas</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de Resultados */}
      <TableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Título</TableCell>
              {!isMobile && <TableCell>Autor</TableCell>}
              <TableCell>Categorías</TableCell>
              <TableCell>Estado</TableCell>
              {!isMobile && <TableCell>Fecha</TableCell>}
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {safeMap(filteredPosts, (post) => (
              <TableRow key={post.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {post.title}
                  </Typography>
                  {isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(post.created_at).toLocaleDateString()}
                    </Typography>
                  )}
                </TableCell>

                {!isMobile && (
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={post.author?.user?.profile_picture}
                        alt={post.author?.user?.first_name}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography variant="body2">
                        {post.author?.user?.first_name || "Desconocido"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {safeMap(post.categories, (cat) => (
                      <Chip
                        key={cat.id}
                        label={cat.name}
                        size="small"
                        sx={{
                          backgroundColor: cat.color || theme.palette.primary.main,
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip
                    label={post.status === 'published' ? 'Publicado' : post.status === 'draft' ? 'Borrador' : 'Programado'}
                    size="small"
                    color={
                      post.status === "published"
                        ? "success"
                        : post.status === "draft"
                        ? "default"
                        : "warning"
                    }
                    variant={post.status === "published" ? "filled" : "outlined"}
                  />
                </TableCell>

                {!isMobile && (
                  <TableCell>
                    {new Date(post.published_at || post.created_at).toLocaleDateString()}
                  </TableCell>
                )}

                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      title="Ver"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    {canEdit() && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setEditPost(post);
                            setEditOpen(true);
                          }}
                          title="Editar"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(post.id)}
                          title="Eliminar"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredPosts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No se encontraron posts que coincidan con los filtros.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Edición */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Editar Post</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Título"
                fullWidth
                value={editPost?.title || ""}
                onChange={(e) =>
                  setEditPost({ ...editPost, title: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Extracto"
                fullWidth
                multiline
                rows={2}
                value={editPost?.excerpt || ""}
                onChange={(e) =>
                  setEditPost({ ...editPost, excerpt: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Contenido (HTML)"
                fullWidth
                multiline
                rows={10}
                value={editPost?.content || ""}
                onChange={(e) =>
                  setEditPost({ ...editPost, content: e.target.value })
                }
                helperText="Puedes editar el contenido HTML directamente aquí."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Estado"
                fullWidth
                value={editPost?.status || "draft"}
                onChange={(e) =>
                  setEditPost({ ...editPost, status: e.target.value })
                }
              >
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
                <MenuItem value="scheduled">Programado</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostManagementTable;
